import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { FolderOpen, FolderLock, FileText, FilePlus, Trash2, User, Shield, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = {
  care_plan: { label: 'Care Plan', level: 1 },
  report: { label: 'Carer Report', level: 1 },
  contract: { label: 'Contract', level: 2 },
  correspondence: { label: 'Correspondence', level: 2 },
};

export default function ClientFolders() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [viewLevel, setViewLevel] = useState('all');
  const [form, setForm] = useState({ title: '', content: '', category: 'care_plan', folder_level: 1 });

  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);

  useEffect(() => { fetchClients(); }, []);
  useEffect(() => { if (selectedClient) fetchDocs(); }, [selectedClient]);

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/org/members?role=client`, { withCredentials: true });
      setClients(data);
      if (data.length > 0) setSelectedClient(data[0].id);
    } catch { toast.error('Failed to load clients'); }
    setLoading(false);
  };

  const fetchDocs = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/client-folders/${selectedClient}`, { withCredentials: true });
      setDocs(data);
    } catch { setDocs([]); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error('Title is required'); return; }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/client-folders`, {
        ...form, client_id: selectedClient
      }, { withCredentials: true });
      toast.success('Document added');
      setShowCreate(false);
      setForm({ title: '', content: '', category: 'care_plan', folder_level: 1 });
      fetchDocs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
    setCreating(false);
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API_URL}/api/client-folders/${docId}`, { withCredentials: true });
      toast.success('Document deleted');
      fetchDocs();
    } catch { toast.error('Failed to delete'); }
  };

  const updateCategory = (cat) => {
    const level = CATEGORIES[cat]?.level || 1;
    setForm(f => ({ ...f, category: cat, folder_level: level }));
  };

  const filtered = viewLevel === 'all' ? docs : docs.filter(d => d.folder_level === parseInt(viewLevel));
  const level1Docs = docs.filter(d => d.folder_level === 1);
  const level2Docs = docs.filter(d => d.folder_level === 2);

  if (loading) return (
    <DashboardLayout><div className="flex justify-center py-20"><div className="spinner w-8 h-8" /></div></DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl" data-testid="client-folders-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">Client Folders</h1>
            <p className="text-muted-foreground mt-1">Two-level access-controlled document folders</p>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" data-testid="add-document-btn">
                <FilePlus className="w-4 h-4 mr-2" /> Add Document
              </Button>
            </DialogTrigger>
            <DialogContent className="glass-card border-border sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-foreground">Add Document to Client Folder</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label className="text-foreground">Title *</Label>
                  <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="mt-1 bg-muted border-border text-foreground" data-testid="folder-doc-title" />
                </div>
                <div>
                  <Label className="text-foreground">Category</Label>
                  <Select value={form.category} onValueChange={updateCategory}>
                    <SelectTrigger className="mt-1 bg-muted border-border text-foreground" data-testid="folder-doc-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="care_plan">Care Plan (Level 1)</SelectItem>
                      <SelectItem value="report">Carer Report (Level 1)</SelectItem>
                      {isAdmin && <SelectItem value="contract">Contract (Level 2 - Admin Only)</SelectItem>}
                      {isAdmin && <SelectItem value="correspondence">Correspondence (Level 2 - Admin Only)</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                <div className="p-3 rounded-lg bg-muted/50 border border-border">
                  <p className="text-xs text-muted-foreground flex items-center gap-2">
                    {form.folder_level === 1 ? (
                      <><Eye className="w-3.5 h-3.5 text-[#00A6A0]" /> <span><strong>Level 1</strong> — Visible to caregivers and admin</span></>
                    ) : (
                      <><EyeOff className="w-3.5 h-3.5 text-amber-500" /> <span><strong>Level 2</strong> — Admin access only</span></>
                    )}
                  </p>
                </div>
                <div>
                  <Label className="text-foreground">Content</Label>
                  <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} className="mt-1 w-full rounded-md bg-muted border border-border text-foreground p-3 text-sm min-h-[120px] resize-none" placeholder="Document content..." data-testid="folder-doc-content" />
                </div>
                <Button type="submit" className="w-full bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white" disabled={creating} data-testid="save-folder-doc-btn">
                  {creating ? 'Saving...' : 'Save Document'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Client Selector */}
        <div className="flex gap-3 items-center flex-wrap">
          <Select value={selectedClient} onValueChange={setSelectedClient}>
            <SelectTrigger className="w-64 bg-card border-border text-foreground" data-testid="client-selector">
              <SelectValue placeholder="Select client" />
            </SelectTrigger>
            <SelectContent>
              {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex gap-1">
            {['all', '1', '2'].map(l => (
              <Button key={l} variant={viewLevel === l ? 'default' : 'ghost'} size="sm"
                className={viewLevel === l ? 'bg-[#00A6A0] text-white' : 'text-muted-foreground'}
                onClick={() => setViewLevel(l)} data-testid={`filter-level-${l}`}
              >
                {l === 'all' ? 'All' : l === '1' ? 'Level 1' : 'Level 2'}
              </Button>
            ))}
          </div>
        </div>

        {/* Level 1 */}
        {(viewLevel === 'all' || viewLevel === '1') && (
          <Card className="glass-card border-border" data-testid="level-1-folder">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-[#00A6A0]/10">
                  <FolderOpen className="w-5 h-5 text-[#00A6A0]" />
                </div>
                <div>
                  <CardTitle className="text-foreground">Level 1 — Care Plans & Reports</CardTitle>
                  <CardDescription>Accessible by assigned caregivers and admin</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {level1Docs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No Level 1 documents</p>
              ) : (
                <div className="space-y-2">
                  {level1Docs.map(doc => (
                    <div key={doc.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/50" data-testid={`folder-doc-${doc.id}`}>
                      <FileText className="w-5 h-5 text-[#00A6A0] mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">{doc.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{CATEGORIES[doc.category]?.label || doc.category} · By {doc.created_by_name} · {new Date(doc.created_at).toLocaleDateString()}</p>
                        {doc.content && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{doc.content}</p>}
                      </div>
                      {isAdmin && (
                        <Button variant="ghost" size="sm" className="text-red-500 hover:bg-red-500/10" onClick={() => handleDelete(doc.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Level 2 - Admin Only */}
        {isAdmin && (viewLevel === 'all' || viewLevel === '2') && (
          <Card className="glass-card border-border border-amber-500/20" data-testid="level-2-folder">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-amber-500/10">
                  <FolderLock className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <CardTitle className="text-foreground">Level 2 — Contracts & Correspondence</CardTitle>
                  <CardDescription>Admin access only — not visible to caregivers</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {level2Docs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No Level 2 documents</p>
              ) : (
                <div className="space-y-2">
                  {level2Docs.map(doc => (
                    <div key={doc.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-amber-500/20" data-testid={`folder-doc-${doc.id}`}>
                      <FileText className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">{doc.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{CATEGORIES[doc.category]?.label || doc.category} · By {doc.created_by_name} · {new Date(doc.created_at).toLocaleDateString()}</p>
                        {doc.content && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{doc.content}</p>}
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:bg-red-500/10" onClick={() => handleDelete(doc.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
